import * as React from 'react'
import { useMemo } from 'react'
import { styled } from 'styled-components'
import { _tempTheme } from '../../utils/tempTheme'
import {
	AnnotatedMarkdown,
	AnnotationUpdateProps,
	ReadOnlyProps,
} from '../AnnotatableMarkdown/Markdown'
import { DEFAULT_JR_CONTROLS_DISABLED_HTML_TAGS } from '../AnnotatableMarkdown/basicMarkdownComponents'
import { CLOSE_ANNOTATION_HELPERS } from '@mission.io/question-toolkit'
import { Nodes } from 'mdast'
import { MarkdownTypes, QuestionProps } from '@mission.io/question-views'
import { getInlineCodeInformation } from '../AnnotatableMarkdown/helpers'

const NODES_REQUIRING_NEW_LINE_AFTER = new Set([
	'paragraph',
	'listItem',
	'blockquote',
	'break',
	'heading',
	'code',
	'break',
])

type TextToSpeechOverrides = Map<
	// tag name
	string,
	MarkdownTypes.TextToSpeechJSXElementRenderer
>

/**
 * convertCodeToString - convert the code of the given language to a string which can be read by text to speech
 *
 * @param {string} codeSnippet - the code to convert
 * @param {string} language - the language the code is in
 *
 * @returns {string}
 */
function convertCodeToString(codeSnippet: string, language: string): string {
	if (language === 'math' || language === 'latex' || language === 'katex') {
		return codeSnippet
			.replace(/\\cdot/g, 'times')
			.replace(/\\times/g, 'times')
			.replace(
				/\\frac{([^}]+)}{([^}]+)}/g,
				(_, numerator, denominator) => `(${numerator}) / (${denominator})`
			)
			.replace(/\\text{([^}]+)}/g, (_, text) => text)
			.replace(/{([^}]+)}/g, (_, parenthetical) => `(${parenthetical})`)
	}
	return codeSnippet
}

/**
 * stripMarkdownSyntax - remove the markdown syntax from the given markdown string
 *
 * @param {string} markdown - the markdown to strip the markdown syntax from
 * @param {TextToSpeechOverrides} overrides - any custom renderers for given tag types
 *
 * @returns {string} a string without all the markdown syntax
 */
export function stripMarkdownSyntax(
	markdown: string,
	overrides: TextToSpeechOverrides
): string {
	try {
		const ast = CLOSE_ANNOTATION_HELPERS.parseMarkdownToAST(markdown)

		const textSegments: string[] = []
		const elementsToHandle: Array<
			| { type: 'node'; node: Nodes }
			// when a text element is encountered, the text will be added to the string. Used for adding text after the children of a node have been handled.
			| { type: 'text'; text: string }
		> = [{ type: 'node', node: ast }]

		/**
		 * addChildrenToStack - pushes children onto the `elementsToHandle` stack
		 *
		 * @param {Array<Nodes>} children - the nodes to add to the stack
		 */
		const addChildrenToStack = (children: Array<Nodes>) => {
			// add in reverse so first child is popped off the stack first
			for (let i = children.length - 1; i >= 0; i--) {
				const child = children[i]

				// typescript index array check
				if (child == null) {
					continue
				}

				elementsToHandle.push({ type: 'node', node: child })
			}
		}

		let currentElement = null
		while ((currentElement = elementsToHandle.pop())) {
			if (currentElement.type === 'text') {
				textSegments.push(currentElement.text)
				continue
			}

			const currentNode = currentElement.node
			if (currentNode.type === 'text') {
				textSegments.push(currentNode.value)
				continue
			}

			if (currentNode.type === 'code') {
				textSegments.push(
					convertCodeToString(currentNode.value, currentNode.lang ?? 'code')
				)
				textSegments.push('\n')
				continue
			}

			if (currentNode.type === 'inlineCode') {
				const codeData = getInlineCodeInformation(currentNode.value, null)
				textSegments.push(
					convertCodeToString(codeData.codeSnippet, codeData.language)
				)
				continue
			}

			if (NODES_REQUIRING_NEW_LINE_AFTER.has(currentNode.type)) {
				elementsToHandle.push({ type: 'text', text: '\n' })
			}

			if (
				(currentNode.type === 'mdxJsxFlowElement' ||
					currentNode.type === 'mdxJsxTextElement') &&
				currentNode.name &&
				overrides.has(currentNode.name)
			) {
				const result = overrides.get(currentNode.name)?.(currentNode)
				if (!result) {
					continue
				}

				if ('text' in result) {
					textSegments.push(result.text)
				}

				if ('children' in result) {
					addChildrenToStack(result.children)
				}
				continue
			}

			if (!('children' in currentNode)) {
				continue
			}

			addChildrenToStack(currentNode.children)
		}

		return textSegments.join('').trim()
	} catch (err) {
		console.error(err)
		return ''
	}
}

/**
 * MarkdownTextToSpeech - adds support for text to speech on markdown fields
 *
 * @param {Object} props - the react props
 * @param {string} props.children - the markdown string
 * @param {?Array<string>} props.disabledComponents? - a list of components to refuse to render
 * @param {TextToSpeechComponent} props.textToSpeech? - a component which handles text to speech
 * @param {MarkdownOverrideBundle} props.overrides? - a mapping of components names to the components which should be used when rendering markdown and text to speech
 * @param {...AnnotatedMarkdownProps} props.* - all other props are passed to the annotated markdown component, see its documentation for what props are allowed.
 *
 * @return {React$Node}
 */
export default function MarkdownTextToSpeech({
	children,
	disabledComponents = DEFAULT_JR_CONTROLS_DISABLED_HTML_TAGS,
	textToSpeech: TextToSpeech,
	overrides,
	className,
	...annotatedMarkdownProps
}: {
	children: string
	disabledComponents?: Array<string>
	textToSpeech?: QuestionProps.TextToSpeechComponent | null
	overrides?: MarkdownTypes.MarkdownOverrideBundle | null | undefined
} & (Omit<ReadOnlyProps, 'customJSXElements' | 'markdown'> &
	(
		| (AnnotationUpdateProps & { readOnly: false })
		| { readOnly?: true }
		| null
	))): JSX.Element {
	const { markdownOverrides, textToSpeechOverrides } = useMemo((): {
		markdownOverrides?: MarkdownTypes.CustomJSXElementLookup
		textToSpeechOverrides?: TextToSpeechOverrides
	} => {
		if (!overrides) {
			return {}
		}

		const markdownOverrides: MarkdownTypes.CustomJSXElementLookup = {}
		const textToSpeechOverrides: TextToSpeechOverrides = new Map()
		Object.entries(overrides).forEach(([tag, renderers]) => {
			markdownOverrides[tag] = renderers.markdown
			textToSpeechOverrides.set(tag, renderers.textToSpeech)
		})

		return {
			markdownOverrides,
			textToSpeechOverrides,
		}
	}, [overrides])

	const strippedMarkdown = useMemo(
		() => stripMarkdownSyntax(children, textToSpeechOverrides ?? new Map()),
		[children, textToSpeechOverrides]
	)

	return (
		<>
			<AnnotatedMarkdown
				customJSXElements={markdownOverrides ?? undefined}
				disabledHtmlTags={disabledComponents}
				className={className}
				markdown={children}
				readOnly={true}
				rootClassName="first:*:mt-0 last:*:mb-0"
				{...annotatedMarkdownProps}
			/>
			{TextToSpeech ? (
				<SpeechWrapper>
					<TextToSpeech>
						<HideThisThisComponent>{strippedMarkdown}</HideThisThisComponent>
					</TextToSpeech>
				</SpeechWrapper>
			) : null}
		</>
	)
}

const HideThisThisComponent = styled.div`
	display: None;
`
const SpeechWrapper = styled.div`
	silent > * {
		display: none;
	}
	silent > speech-button {
		display: visible;
	}
	margin: ${_tempTheme.spacing2};
`
